import { Helmet } from "react-helmet-async";

import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const BUSINESS = {
  legalName: "Sagar Lifestyle NY LLC",
  displayName: "Sagar Lifestyle NY",
  registeredAddress: "7912 5th Avenue, Bay Ridge, Brooklyn, NY 11209",
  returnAddress: "7912 5th Avenue, Bay Ridge, Brooklyn, NY 11209",
  supportEmail: "sagarfashion777@gmail.com",
  businessEmail: "sagarfashion777@gmail.com",
  phone: "+1 (718) 630-1100",
  supportHours: "Mon–Fri 11:00 AM–6:00 PM, Sat–Sun 11:00 AM–7:00 PM (ET)",
  governingState: "New York",
};

const DATES = {
  shippingPolicyUpdated: "[EFFECTIVE DATE]",
  termsUpdated: "[EFFECTIVE DATE]",
  privacyUpdated: "[EFFECTIVE DATE]",
};

const SHIPPING = {
  defaultFlatCharge: 9.0,
  freeShippingThreshold: 100.0,
  processingTime: "[ORDER PROCESSING TIME]",
  returnWindow: "[RETURN WINDOW]",
  refundProcessingTime: "[REFUND PROCESSING TIME]",
  cancellationRule: "[CANCELLATION RULE]",
  deliveryCountries: "[DELIVERY COUNTRIES]",
  internationalShippingSupported: false,
  exampleWeightTiers: [
    { range: "0 – 500 g", charge: "$4.99" },
    { range: "501 g – 1 kg", charge: "$7.99" },
    { range: "1 kg – 2 kg", charge: "$11.99" },
    { range: "2 kg – 5 kg", charge: "$18.99" },
    { range: "Above 5 kg", charge: "$2.50 per additional kg" },
  ],
};

const PROVIDERS = {
  paymentProcessor: "Stripe",
  shippingPartners: "[SHIPPING PROVIDER]",
  analyticsProvider: "[ANALYTICS PROVIDER]",
  emailServiceProvider: "[EMAIL SERVICE PROVIDER]",
  smsWhatsappEnabled: false,
};

const SOCIAL = {
  instagram: "[INSTAGRAM URL]",
  facebook: "[FACEBOOK URL]",
  whatsapp: "[WHATSAPP URL]",
};

const POLICY_NAV = [
  { label: "Shipping Policy", path: "/shipping-policy" },
  { label: "Terms & Conditions", path: "/terms" },
  { label: "Privacy Policy", path: "/privacy" },
];

function PolicyNavigation() {
  const { pathname } = useLocation();
  return (
    <nav aria-label="Policy pages" className="border-b border-stone-100 bg-white sticky top-0 z-20">
      <div className="max-w-5xl mx-auto px-4">
        <div className="flex gap-1 overflow-x-auto no-scrollbar py-3 -mx-1">
          {POLICY_NAV.map(item => {
            const active = pathname === item.path;
            return (
              <Link key={item.path} to={item.path} aria-current={active ? "page" : undefined}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${active ? "bg-stone-900 text-white" : "text-stone-500 hover:bg-stone-100 hover:text-stone-900"}`}>
                {item.label}
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
}

function LastUpdated({ date }) {
  return <p className="text-xs text-stone-400 font-medium">Last updated: <span className={date?.startsWith("[") ? "text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded" : "text-stone-600"}>{date}</span></p>;
}

function PolicyHero({ eyebrow, title, subtitle, updated }) {
  return (
    <header className="max-w-3xl mx-auto px-4 pt-12 pb-6 text-center">
      <p className="text-xs font-bold uppercase tracking-widest text-rose-500 mb-3">{eyebrow}</p>
      <h1 className="serif text-3xl md:text-4xl font-bold text-stone-900 mb-4">{title}</h1>
      <p className="text-stone-500 text-base leading-relaxed">{subtitle}</p>
      <div className="mt-5 flex justify-center"><LastUpdated date={updated} /></div>
    </header>
  );
}

function QuickSummary({ points }) {
  return <div className="max-w-3xl mx-auto px-4 mb-10"><div className="bg-stone-50 border border-stone-200 rounded-3xl p-6"><p className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-3">In short</p><ul className="space-y-2">{points.map((p,i)=><li key={i} className="flex items-start gap-2.5 text-sm text-stone-700 leading-relaxed"><svg className="w-4 h-4 mt-0.5 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg><span>{p}</span></li>)}</ul></div></div>;
}

function PolicyTableOfContents({ sections, activeId }) {
  return <><div className="lg:hidden max-w-3xl mx-auto px-4 mb-8"><div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">{sections.map(s=><a key={s.id} href={`#${s.id}`} className={`flex-shrink-0 px-3.5 py-2 rounded-full text-xs font-semibold whitespace-nowrap border transition-colors ${activeId===s.id ? "bg-stone-900 text-white border-stone-900" : "text-stone-500 border-stone-200 hover:border-stone-400"}`}>{s.title}</a>)}</div></div><nav aria-label="Table of contents" className="hidden lg:block"><div className="sticky top-24 w-56"><p className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-3 px-2">On this page</p><ul className="space-y-0.5">{sections.map(s=><li key={s.id}><a href={`#${s.id}`} className={`block px-3 py-1.5 rounded-lg text-sm transition-colors ${activeId===s.id ? "bg-stone-100 text-stone-900 font-semibold" : "text-stone-500 hover:text-stone-900 hover:bg-stone-50"}`}>{s.title}</a></li>)}</ul></div></nav></>;
}

function useActiveSection(sectionIds) {
  const [activeId,setActiveId]=useState(sectionIds[0]);
  useEffect(()=>{const observer=new IntersectionObserver(entries=>entries.forEach(entry=>{if(entry.isIntersecting)setActiveId(entry.target.id)}),{rootMargin:"-15% 0px -70% 0px"});sectionIds.forEach(id=>{const el=document.getElementById(id);if(el)observer.observe(el)});return()=>observer.disconnect()},[sectionIds]);
  return activeId;
}

function PolicySection({ id, number, title, children }) {
  return <section id={id} className="mb-12 scroll-mt-24"><div className="flex items-baseline gap-3 mb-4">{number&&<span className="serif text-lg text-rose-400 font-bold">{number}</span>}<h2 className="serif text-xl md:text-2xl font-bold text-stone-900">{title}</h2></div><div className="prose-policy text-stone-600 text-sm md:text-[15px] leading-relaxed space-y-3">{children}</div></section>;
}

function InfoCard({ icon, title, children, tone="neutral" }) {
  const tones={neutral:"bg-stone-50 border-stone-200",amber:"bg-amber-50 border-amber-200",rose:"bg-rose-50 border-rose-200"};
  return <div className={`rounded-2xl border p-5 my-4 ${tones[tone]}`}><div className="flex items-start gap-3">{icon&&<span className="text-xl leading-none">{icon}</span>}<div className="flex-1">{title&&<p className="font-semibold text-stone-900 text-sm mb-1">{title}</p>}<div className="text-sm text-stone-600 leading-relaxed">{children}</div></div></div></div>;
}

function TrustCard({ icon, title, children }) {
  return <div className="bg-white rounded-2xl border border-stone-100 p-5 text-center"><div className="w-11 h-11 rounded-xl bg-stone-50 flex items-center justify-center text-xl mx-auto mb-3">{icon}</div><p className="font-semibold text-stone-900 text-sm mb-1.5">{title}</p><p className="text-xs text-stone-500 leading-relaxed">{children}</p></div>;
}

function TrustStrip() {
  return <div className="max-w-5xl mx-auto px-4 py-12"><div className="text-center mb-8"><h2 className="serif text-2xl font-bold text-stone-900 mb-2">How we look after your order</h2><p className="text-stone-500 text-sm">The same four commitments apply across every policy on this site.</p></div><div className="grid grid-cols-2 md:grid-cols-4 gap-4"><TrustCard icon="🔒" title="Secure Shopping">We use reasonable safeguards to protect the information used to run your account and orders.</TrustCard><TrustCard icon="📦" title="Order Visibility">Track your order from shipment through delivery whenever tracking is available.</TrustCard><TrustCard icon="🤝" title="Customer Support">If something goes wrong, contact us and we'll investigate and help.</TrustCard><TrustCard icon="💳" title="Transparent Charges">Shipping charges and order totals are shown before you complete payment.</TrustCard></div></div>;
}

function PolicyTable({ columns, rows }) {
  return <div className="my-4"><div className="hidden sm:block overflow-x-auto rounded-2xl border border-stone-200"><table className="w-full text-sm"><thead><tr className="bg-stone-50 border-b border-stone-200">{columns.map((c,i)=><th key={i} className="text-left px-4 py-3 font-semibold text-stone-500 uppercase text-xs tracking-wide">{c}</th>)}</tr></thead><tbody>{rows.map((row,i)=><tr key={i} className="border-b border-stone-100 last:border-0">{row.map((cell,j)=><td key={j} className="px-4 py-3 text-stone-700">{cell}</td>)}</tr>)}</tbody></table></div><div className="sm:hidden space-y-3">{rows.map((row,i)=><div key={i} className="rounded-2xl border border-stone-200 p-4 bg-white">{row.map((cell,j)=><div key={j} className="flex justify-between py-1 text-sm"><span className="text-stone-400">{columns[j]}</span><span className="text-stone-800 font-medium text-right">{cell}</span></div>)}</div>)}</div></div>;
}

function PolicyFAQ({ title="Frequently asked questions", items }) {
  const [openIndex,setOpenIndex]=useState(null);
  return <div className="max-w-3xl mx-auto px-4 py-4"><h2 className="serif text-2xl font-bold text-stone-900 mb-6">{title}</h2><div className="space-y-2">{items.map((item,i)=>{const open=openIndex===i;return <div key={i} className="border border-stone-200 rounded-2xl overflow-hidden"><button onClick={()=>setOpenIndex(open?null:i)} aria-expanded={open} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-stone-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400"><span className="font-medium text-stone-900 text-sm">{item.q}</span><svg className={`w-4 h-4 text-stone-400 flex-shrink-0 transition-transform duration-200 ${open?"rotate-180":""}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg></button>{open&&<div className="px-5 pb-4 text-sm text-stone-600 leading-relaxed">{item.a}</div>}</div>})}</div></div>;
}

function SocialLinks() {
  const links=[{label:"Instagram",value:SOCIAL.instagram},{label:"Facebook",value:SOCIAL.facebook},{label:"WhatsApp",value:SOCIAL.whatsapp}];
  return <div className="mt-5 flex flex-wrap justify-center gap-2">{links.map(link=>link.value?.startsWith("[")?<span key={link.label} className="px-4 py-2 rounded-full bg-white/5 text-amber-400 text-xs font-semibold">{link.label}: {link.value}</span>:<a key={link.label} href={link.value} target="_blank" rel="noreferrer" className="px-4 py-2 rounded-full bg-white/10 text-white text-xs font-semibold hover:bg-white/20 transition-colors">{link.label}</a>)}</div>;
}

function PolicyContact() {
  const rows=[{label:"Support email",value:BUSINESS.supportEmail},{label:"Business email",value:BUSINESS.businessEmail},{label:"Phone",value:BUSINESS.phone},{label:"Business address",value:BUSINESS.registeredAddress},{label:"Support hours",value:BUSINESS.supportHours}];
  return <div className="max-w-3xl mx-auto px-4 py-12"><div className="bg-stone-900 rounded-3xl p-8 md:p-10 text-center"><h2 className="serif text-2xl font-bold text-white mb-2">Questions about this policy?</h2><p className="text-stone-300 text-sm mb-6">We're here to help — reach out any time.</p><div className="grid sm:grid-cols-2 gap-3 text-left max-w-md mx-auto">{rows.map(r=><div key={r.label} className="bg-white/5 rounded-xl px-4 py-3"><p className="text-[11px] text-stone-400 uppercase tracking-wide mb-0.5">{r.label}</p><p className={`text-sm font-medium ${r.value?.startsWith("[")?"text-amber-400":"text-white"}`}>{r.value}</p></div>)}</div><SocialLinks /></div></div>;
}

function RelatedPolicies({ exclude }) {
  return <div className="max-w-3xl mx-auto px-4 pb-16"><p className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-3">Related policies</p><div className="flex flex-wrap gap-2">{POLICY_NAV.filter(p=>p.label!==exclude).map(p=><Link key={p.path} to={p.path} className="px-4 py-2 rounded-full border border-stone-200 text-sm font-medium text-stone-600 hover:border-rose-300 hover:text-rose-500 transition-colors">{p.label} →</Link>)}</div></div>;
}

function BackToTop() {
  const [visible,setVisible]=useState(false);
  useEffect(()=>{const onScroll=()=>setVisible(window.scrollY>600);window.addEventListener("scroll",onScroll);return()=>window.removeEventListener("scroll",onScroll)},[]);
  if(!visible)return null;
  return <button onClick={()=>window.scrollTo({top:0,behavior:"smooth"})} aria-label="Back to top" className="fixed bottom-6 right-6 w-11 h-11 rounded-full bg-stone-900 text-white shadow-lg flex items-center justify-center hover:bg-rose-500 transition-colors z-30"><svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" /></svg></button>;
}

function Placeholder({ value }) {
  const isUnfilled=typeof value==="string"&&value.startsWith("[");
  if(!isUnfilled)return <>{value}</>;
  return <span className="inline-block px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-xs font-semibold align-middle">{value}</span>;
}

function PolicyLayout({ children }) {
  return <div className="min-h-screen bg-white"><style>{`.no-scrollbar{scrollbar-width:none;-ms-overflow-style:none}.no-scrollbar::-webkit-scrollbar{display:none}.prose-policy ul,.prose-policy ol{margin-top:.25rem}.prose-policy a{text-underline-offset:2px}@media(prefers-reduced-motion:reduce){*,*::before,*::after{transition-duration:.01ms!important;animation-duration:.01ms!important}}`}</style><PolicyNavigation />{children}<BackToTop /></div>;
}

const SECTIONS = [
  { id: "promise", title: "Our Shipping Promise" },
  { id: "charges", title: "Shipping Charges" },
  { id: "weight-tiers", title: "Weight-Based Shipping" },
  { id: "free-shipping", title: "Free Shipping" },
  { id: "processing", title: "Order Processing" },
  { id: "delivery-times", title: "Delivery Times" },
  { id: "tracking", title: "Tracking" },
  { id: "verification", title: "Delivery Verification" },
  { id: "issues", title: "Lost, Damaged or Missing Packages" },
  { id: "address", title: "Incorrect Shipping Address" },
  { id: "international", title: "International Shipping" },
  { id: "restricted", title: "Restricted Destinations" },
  { id: "faq", title: "FAQ" },
];

export default function ShippingPolicy() {
  const activeId = useActiveSection(SECTIONS.map(s => s.id));

  return (
    <PolicyLayout>
      <Helmet>
        <title>Shipping Policy | {BUSINESS.legalName}</title>
        <meta name="description" content="How Sagar Lifestyle NY LLC calculates shipping, processes orders, and supports you through delivery." />
        <link rel="canonical" href="[CANONICAL URL]" />
        <meta property="og:title" content={`Shipping Policy | ${BUSINESS.legalName}`} />
        <meta property="og:description" content="Clear shipping charges, delivery timelines, and support from checkout through delivery." />
      </Helmet>

      <PolicyHero
        eyebrow="Shipping"
        title="Shipping Policy"
        subtitle="Clear shipping information, transparent charges, and support from checkout through delivery."
        updated={DATES.shippingPolicyUpdated}
      />

      <QuickSummary
        points={[
          "Shipping charges are calculated at checkout and shown before you pay — never added as a surprise.",
          `Orders over $${SHIPPING.freeShippingThreshold.toFixed(0)} currently ship free; this threshold can change with promotions.`,
          "Tracking is provided when available so you can follow your order after it ships.",
          "If something goes wrong in transit, contact us — we investigate every reported issue.",
        ]}
      />

      <div className="max-w-5xl mx-auto px-4 grid lg:grid-cols-[224px_1fr] gap-10">
        <PolicyTableOfContents sections={SECTIONS} activeId={activeId} />

        <div className="max-w-2xl">
          <PolicySection id="promise" number="1" title="Our Shipping Promise">
            <p>
              Once your payment is successfully processed, your order moves into our fulfillment
              queue and you'll receive an order confirmation. As soon as your package is handed to
              our delivery partner, we send a shipping confirmation with tracking information when
              it's available, so you can follow your order's progress.
            </p>
            <p>
              We take shipping issues seriously. If your shipment is lost, damaged, delayed, or
              delivered incorrectly, contact our support team and we will investigate the issue and
              work with the relevant delivery partner to reach a resolution.
            </p>
            <InfoCard icon="ℹ️" tone="neutral">
              We coordinate your shipment through delivery and work with you and the carrier to
              resolve verified delivery issues. Once a package leaves our facility, transit is
              carried out by our delivery partners under their own operating conditions.
            </InfoCard>
          </PolicySection>

          <PolicySection id="charges" number="2" title="Shipping Charges">
            <p>Shipping charges can depend on a combination of factors, including:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Your order value</li>
              <li>The total weight of your shipment</li>
              <li>Your delivery destination</li>
              <li>The shipping method selected</li>
              <li>Active promotions</li>
              <li>Other shipping rules configured for your order</li>
            </ul>
            <p>
              Whatever the final calculation, your exact shipping cost is always displayed on the
              checkout page <strong>before</strong> you complete payment — you'll never be charged a
              shipping amount you haven't already seen and agreed to.
            </p>
          </PolicySection>

          <PolicySection id="weight-tiers" number="3" title="Admin-Configurable Weight-Based Shipping">
            <p>
              Our store can calculate shipping based on the combined weight of the items in your
              order. Each product carries a stored weight, and your order's shipping charge is
              matched against the current weight tiers. The table below is an{" "}
              <strong>example</strong> of how this system works — actual tiers in effect may differ
              and are configured on an ongoing basis:
            </p>
            <PolicyTable
              columns={["Order Weight", "Example Shipping Charge"]}
              rows={SHIPPING.exampleWeightTiers.map(t => [t.range, t.charge])}
            />
            <InfoCard icon="⚖️" tone="amber" title="These figures are illustrative">
              The charges above are examples of how weight-based shipping works, not a fixed price
              list. The shipping charge shown at your checkout reflects the rules currently
              configured for your order and destination at the time you place it.
            </InfoCard>
          </PolicySection>

          <PolicySection id="free-shipping" number="4" title="Free Shipping">
            <p>
              We may offer free shipping when your order meets a configured minimum purchase amount.
              Currently, orders above{" "}
              <strong>${SHIPPING.freeShippingThreshold.toFixed(0)}</strong> qualify for free
              shipping. This threshold can vary depending on promotions, destination, product, or
              shipping method, and may change over time.
            </p>
            <p>The applicable shipping charge — free or otherwise — is always shown at checkout before you pay.</p>
          </PolicySection>

          <PolicySection id="processing" number="5" title="Order Processing">
            <p>Here's what happens after you place an order:</p>
            <ol className="list-decimal pl-5 space-y-1">
              <li>Your order is placed and submitted for payment.</li>
              <li>Payment is confirmed.</li>
              <li>Your order is reviewed and processed.</li>
              <li>Your items are prepared and packed.</li>
              <li>The shipment is handed to our delivery partner.</li>
              <li>Tracking information becomes available.</li>
              <li>Your order is delivered.</li>
            </ol>
            <p>
              Typical order processing time: <Placeholder value={SHIPPING.processingTime} />. This is
              the time it takes us to prepare your order for handoff to the carrier — it does not
              include transit time (see below).
            </p>
          </PolicySection>

          <PolicySection id="delivery-times" number="6" title="Delivery Times">
            <p>It's worth understanding the difference between two separate timeframes:</p>
            <div className="grid sm:grid-cols-2 gap-3 my-4">
              <InfoCard icon="📦" title="Processing Time">
                The time we take to prepare, pack, and hand off your order to the delivery partner.
              </InfoCard>
              <InfoCard icon="🚚" title="Transit Time">
                The time the delivery partner takes to carry your package from our facility to your
                address, once it has shipped.
              </InfoCard>
            </div>
            <p>
              Estimated delivery windows are shown at checkout based on your destination and shipping
              method. Actual delivery can be affected by:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Carrier delays outside our control</li>
              <li>Weather conditions</li>
              <li>Holidays and high-volume periods</li>
              <li>Customs processing, where applicable</li>
              <li>Incorrect or incomplete delivery addresses</li>
              <li>Remote or hard-to-access delivery areas</li>
            </ul>
            <p>We do not guarantee an exact delivery date unless that guarantee is explicitly stated on your order.</p>
          </PolicySection>

          <PolicySection id="tracking" number="7" title="Tracking">
            <p>
              Once your order ships, you'll receive a tracking number and a link to follow its
              progress, when tracking is provided by the carrier for your shipping method.
            </p>
            <p>If your tracking information doesn't update for an extended period, or shows "delivered" but you haven't received your package:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Check with others at the delivery address, and any secure delivery locations (mailroom, front desk, safe drop spot).</li>
              <li>Wait a short period, as tracking scans can occasionally register before physical delivery.</li>
              <li>Contact our support team with your order number so we can look into it with the carrier.</li>
            </ul>
          </PolicySection>

          <PolicySection id="verification" number="8" title="Delivery Verification & Customer Protection">
            <p>To help us resolve delivery issues quickly, we ask customers to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Check the package's condition when it's safe and possible to do so.</li>
              <li>Report visible damage as soon as you notice it.</li>
              <li>Contact support promptly if a package appears to be missing.</li>
              <li>Keep the original packaging and any damaged items until the issue is resolved.</li>
              <li>Have your order number and tracking details ready when reaching out.</li>
            </ul>
            <p>We investigate verified delivery problems and coordinate with our shipping partners to work toward a resolution.</p>
          </PolicySection>

          <PolicySection id="issues" number="9" title="Lost, Damaged, or Missing Packages">
            <div className="space-y-5">
              <div>
                <h3 className="font-semibold text-stone-900 text-sm mb-1">Marked delivered but not received</h3>
                <p>Contact us with your order details. We'll review the delivery record with the carrier and work with you toward a resolution.</p>
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 text-sm mb-1">Damaged during transit</h3>
                <p>Photograph the item and packaging, and contact support as soon as possible so we can start a claim with the carrier.</p>
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 text-sm mb-1">Lost in transit</h3>
                <p>If tracking shows no movement for an extended period, we'll open an investigation with the carrier and keep you updated on next steps.</p>
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 text-sm mb-1">Delivered to the wrong location</h3>
                <p>Let us know right away. We'll work with the carrier to trace the shipment and determine the appropriate resolution.</p>
              </div>
            </div>
          </PolicySection>

          <PolicySection id="address" number="10" title="Incorrect Shipping Address">
            <p>
              You're responsible for providing an accurate, complete shipping address at checkout. If
              an order is delayed, misdelivered, or returned due to an incorrect address provided by
              the customer, additional charges or delays may apply.
            </p>
            <p>
              Address changes may be possible before an order ships — contact support as soon as
              possible if you need to update an address. Once an order has been dispatched, changes
              are generally <Placeholder value={SHIPPING.cancellationRule} />.
            </p>
          </PolicySection>

          <PolicySection id="international" number="11" title="International Shipping">
            {SHIPPING.internationalShippingSupported ? (
              <>
                <p>We ship to select international destinations. For international orders, please note:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Customs duties, taxes, and import fees may apply and are the customer's responsibility unless stated otherwise.</li>
                  <li>Customs processing can add to delivery timelines.</li>
                  <li>Some products or destinations may not be eligible for international delivery.</li>
                </ul>
              </>
            ) : (
              <InfoCard icon="🌍" tone="amber" title="International shipping availability">
                International shipping availability is <Placeholder value="[CONFIGURABLE]" /> — this
                section will be updated once international shipping is confirmed and configured.
              </InfoCard>
            )}
          </PolicySection>

          <PolicySection id="restricted" number="12" title="Restricted / Undeliverable Destinations">
            <p>
              Certain products or destinations may be subject to shipping restrictions based on
              carrier policies, legal requirements, or delivery feasibility. If an item you've
              ordered cannot be delivered to your address, we will contact you to resolve the issue.
            </p>
          </PolicySection>

          <div className="lg:hidden">
            {/* spacing before FAQ on mobile */}
          </div>
        </div>
      </div>

      <PolicyFAQ
        title="Shipping FAQ"
        items={[
          { q: "How much does shipping cost?", a: "Shipping cost depends on your order value, weight, destination, and any active promotions. The exact charge is shown at checkout before you pay." },
          { q: "How is shipping calculated?", a: "Our system applies the shipping rules currently configured for your order, which may be a flat rate or based on the total weight of your shipment." },
          { q: "Do you offer free shipping?", a: `Yes — orders above $${SHIPPING.freeShippingThreshold.toFixed(0)} currently qualify for free shipping. This threshold can change with promotions.` },
          { q: "When will my order ship?", a: <>Orders are typically processed within <Placeholder value={SHIPPING.processingTime} />, then handed to our delivery partner.</> },
          { q: "How do I track my order?", a: "You'll receive a tracking link by email once your order ships, when tracking is available for your shipping method." },
          { q: "What if tracking says delivered but I didn't receive it?", a: "Check with others at the address and any secure drop locations, then contact support with your order number so we can investigate with the carrier." },
          { q: "What if my package arrives damaged?", a: "Photograph the damage and packaging and contact support right away so we can open a claim with the carrier." },
          { q: "Can I change my shipping address?", a: "You can request an address change before your order ships by contacting support. Changes after dispatch are generally not possible." },
          { q: "What happens if my order is delayed?", a: "We'll do our best to keep you informed. Delays can happen due to carrier issues, weather, holidays, or high-volume periods outside our control." },
          { q: "Do you ship internationally?", a: SHIPPING.internationalShippingSupported ? "Yes, to select destinations." : "International shipping availability is being finalized — check back or contact support for the latest status." },
          { q: "Are customs charges included?", a: "Customs duties, taxes, and import fees, where applicable, are generally the customer's responsibility unless stated otherwise at checkout." },
          { q: "Who should I contact about a shipping issue?", a: <>Reach our support team at <Placeholder value={BUSINESS.supportEmail} /> with your order number.</> },
        ]}
      />

      <TrustStrip />
      <PolicyContact />
      <RelatedPolicies exclude="Shipping Policy" />
    </PolicyLayout>
  );
}